/**
 * SKU Generation Utilities
 *
 * Provides functions to auto-generate SKUs from product names
 * following backend validation rules (max 100 chars, alphanumeric + hyphens/underscores)
 */

/**
 * Generates a random 4-digit numeric suffix for SKU uniqueness
 * @returns {string} 4-digit random number as string (e.g., "4728")
 */
function generateRandomSuffix(): string {
  return Math.floor(1000 + Math.random() * 9000).toString();
}

/**
 * Sanitizes a string to match SKU validation rules
 * - Converts to uppercase
 * - Replaces spaces with hyphens
 * - Removes all characters except alphanumeric, hyphens, and underscores
 * - Trims to max length
 *
 * @param {string} input - The string to sanitize
 * @param {number} maxLength - Maximum length (default: 100)
 * @returns {string} Sanitized SKU string
 */
export function sanitizeSku(input: string, maxLength: number = 100): string {
  if (!input || input.trim().length === 0) {
    return '';
  }

  return input
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '-')  // Replace spaces with hyphens
    .replace(/[^A-Z0-9\-_]/g, '')  // Remove invalid characters
    .replace(/-+/g, '-')  // Replace multiple consecutive hyphens with single hyphen
    .replace(/^-+|-+$/g, '')  // Remove leading/trailing hyphens
    .substring(0, maxLength);  // Truncate to max length
}

/**
 * Generates a SKU suggestion from a product name
 *
 * Algorithm:
 * 1. Converts name to uppercase
 * 2. Replaces spaces with hyphens
 * 3. Removes special characters (keeps only alphanumeric, hyphens, underscores)
 * 4. Adds a random 4-digit suffix for uniqueness
 * 5. Truncates to 100 characters if needed
 *
 * Examples:
 * - "iPhone 15 Pro Max" -> "IPHONE-15-PRO-MAX-7234"
 * - "Product@Name#123" -> "PRODUCTNAME123-4856"
 * - "Café Latté" -> "CAF-LATT-9182"
 *
 * @param {string} name - The product name
 * @returns {string} Generated SKU (empty string if name is empty)
 */
export function generateSkuFromName(name: string): string {
  if (!name || name.trim().length === 0) {
    return '';
  }

  // Sanitize the name (leave room for suffix)
  const sanitized = sanitizeSku(name, 95);

  if (sanitized.length === 0) {
    // If sanitization resulted in empty string (e.g., only special chars),
    // use a generic prefix
    return `PROD-${generateRandomSuffix()}`;
  }

  // Add random suffix for uniqueness
  const suffix = generateRandomSuffix();
  const sku = `${sanitized}-${suffix}`;

  // Ensure total length doesn't exceed 100
  return sku.substring(0, 100);
}
